import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, PlayCircle, ArrowRight, Calendar, Play } from "lucide-react";
import api, { resolveMedia } from "@/lib/api";
import useSEO, { stripHtml } from "@/lib/seo";
import SocialShare from "@/components/SocialShare";

function fmtDate(iso, fallback) {
  if (!iso) return fallback || "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return fallback || iso;
    return d.toLocaleDateString("de-DE", { year: "numeric", month: "long" });
  } catch { return fallback || iso; }
}

function coverImage(item) {
  if (item.header_image_url) return resolveMedia(item.header_image_url);
  for (const m of item.media || []) {
    if (m.type === "image" && m.url) return resolveMedia(m.url);
    if (m.type === "video" && m.thumbnail_url) return resolveMedia(m.thumbnail_url);
  }
  return "";
}

function mediaCounts(item) {
  const imgs = (item.media || []).filter((m) => m.type === "image").length + (item.header_image_url ? 1 : 0);
  const vids = (item.media || []).filter((m) => m.type === "video").length;
  return { imgs, vids };
}

function EntryCard({ item, index }) {
  const cover = coverImage(item);
  const { imgs, vids } = mediaCounts(item);
  return (
    <Link
      to={`/bauchroniken/${item.slug}`}
      data-testid={`bauchronik-card-${item.slug}`}
      className="group relative block bg-white border border-brand-line overflow-hidden transition-shadow hover:shadow-[0_20px_40px_-24px_rgba(0,0,0,0.20)]"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
        {cover ? (
          <img
            src={cover}
            alt={item.title}
            loading={index < 3 ? "eager" : "lazy"}
            className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs">
            <Camera className="h-6 w-6" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        {(imgs > 0 || vids > 0) && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1.5 text-[11px] font-medium text-white">
            {imgs > 0 && (
              <span className="inline-flex items-center gap-1 bg-black/55 backdrop-blur px-2 py-0.5">
                <Camera className="h-3 w-3" /> {imgs}
              </span>
            )}
            {vids > 0 && (
              <span className="inline-flex items-center gap-1 bg-black/55 backdrop-blur px-2 py-0.5">
                <PlayCircle className="h-3 w-3" /> {vids}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="px-5 py-4">
        <p className="text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--brand-green-strong))] inline-flex items-center gap-1.5 mb-1.5">
          <Calendar className="h-3 w-3" />
          {item.date || fmtDate(item.date_iso, "")}
        </p>
        <h3 className="font-semibold text-lg leading-snug text-neutral-900 mb-1 group-hover:text-[hsl(var(--brand-green-strong))] transition-colors">
          {item.title}
        </h3>
        <p className="text-xs text-neutral-500 line-clamp-2">
          {stripHtml(item.description || "", 140)}
        </p>
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[hsl(var(--brand-green-strong))]">
          Ansehen <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </Link>
  );
}

export default function Bauchroniken() {
  const [page, setPage] = useState({});
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/bauchroniken/page"), api.get("/bauchroniken")])
      .then(([p, l]) => { setPage(p.data || {}); setItems(l.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useSEO({
    title: "Bauchroniken — Fotos & Videos der Bauentwicklung",
    description: stripHtml(page.intro || "", 200) || "Foto- und videografische Dokumentation der Bauentwicklung am Quartier am Hornschen Tor in Detmold.",
    path: "/bauchroniken",
    image: page.header_image_url ? resolveMedia(page.header_image_url) : undefined,
  });

  const HEADER_IMG = page.header_image_url ? resolveMedia(page.header_image_url) : "/assets/info/01-baugrube.jpg";

  // Extract YouTube / Vimeo video id (for optional hero video)
  const heroVideo = (() => {
    const url = page.header_video_url || "";
    if (!url) return null;
    const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{6,})/);
    if (yt) {
      return {
        provider: "youtube",
        embedUrl: `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&mute=1&rel=0`,
        thumbUrl: page.header_video_thumbnail_url ? resolveMedia(page.header_video_thumbnail_url) : `https://i.ytimg.com/vi/${yt[1]}/maxresdefault.jpg`,
      };
    }
    const vim = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vim) {
      return {
        provider: "vimeo",
        embedUrl: `https://player.vimeo.com/video/${vim[1]}?autoplay=1&muted=1`,
        thumbUrl: page.header_video_thumbnail_url ? resolveMedia(page.header_video_thumbnail_url) : HEADER_IMG,
      };
    }
    return null;
  })();
  const [videoOpen, setVideoOpen] = React.useState(false);

  return (
    <div className="bg-white" data-testid="bauchroniken-page">
      {/* HERO */}
      <section className="relative h-[46vh] sm:h-[54vh] min-h-[320px] max-h-[560px] overflow-hidden bg-neutral-900" data-testid="bauchroniken-hero">
        {heroVideo && videoOpen ? (
          <iframe
            src={heroVideo.embedUrl}
            title="Bauchroniken Video"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            data-testid="bauchroniken-hero-video"
          />
        ) : (
          <>
            <img
              src={heroVideo?.thumbUrl || HEADER_IMG}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover opacity-95"
              loading="eager"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.15) 30%, rgba(0,0,0,0.05) 55%, rgba(255,255,255,0.98) 100%)",
              }}
            />
            {heroVideo && (
              <button
                onClick={() => setVideoOpen(true)}
                data-testid="bauchroniken-hero-play"
                className="absolute inset-0 flex items-center justify-center group"
                aria-label="Video abspielen"
              >
                <span className="flex items-center justify-center h-20 w-20 rounded-full bg-white/95 text-[hsl(var(--brand-green-strong))] shadow-[0_10px_40px_rgba(0,0,0,0.35)] transition-transform group-hover:scale-110">
                  <Play className="h-8 w-8 ml-1" fill="currentColor" />
                </span>
              </button>
            )}
          </>
        )}
        <div className="absolute inset-0 flex items-end pointer-events-none">
          <div className="container-narrow pb-8 lg:pb-14 w-full">
            <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--brand-green))] mb-3 drop-shadow-md">
              Foto- &amp; Videodokumentation
            </p>
            <h1 className="font-semibold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.03] max-w-3xl drop-shadow-[0_2px_20px_rgba(0,0,0,0.35)]">
              Bauchroniken
            </h1>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="container-narrow py-12 lg:py-16 max-w-3xl">
        <p className="text-lg sm:text-xl text-neutral-700 leading-relaxed" data-testid="bauchroniken-intro">
          {page.intro}
        </p>
        <div className="mt-6 flex items-center gap-3">
          <SocialShare
            url={`${window.location.origin}/bauchroniken`}
            title="Bauchroniken — Quartier am Hornschen Tor"
            text={stripHtml(page.intro || "", 160)}
            testid="bauchroniken-share"
          />
          <span className="text-xs text-neutral-500">{items.length} {items.length === 1 ? "Eintrag" : "Einträge"}</span>
        </div>
      </section>

      {/* TIMELINE / GRID */}
      <section className="container-narrow pb-16 lg:pb-24">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0,1,2,3,4,5].map((i) => (
              <div key={i} className="animate-pulse bg-neutral-100 border border-brand-line aspect-[4/3]" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="border border-brand-line bg-neutral-50 px-8 py-16 text-center" data-testid="bauchroniken-empty">
            <Camera className="h-8 w-8 mx-auto mb-4 text-neutral-400" />
            <p className="text-neutral-600">Die ersten Bauchronik-Einträge werden in Kürze veröffentlicht.</p>
          </div>
        ) : (
          <>
            {/* Timeline separator line on desktop */}
            <div className="relative">
              <div
                aria-hidden="true"
                className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-brand-line to-transparent"
                style={{ transform: "translateX(-0.5px)" }}
              />
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="bauchroniken-grid">
                {items.map((item, i) => (
                  <EntryCard key={item.id} item={item} index={i} />
                ))}
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
